// Default light direction in view space: right (+1), no up, toward viewer (+1) — normalized.
const DEFAULT_LIGHT_DIR_VIEW = (() => {
    const x = 1, y = 0, z = 1;
    const len = Math.sqrt(x * x + y * y + z * z);
    return { x: x / len, y: y / len, z: z / len };
})();
const MIN_SHADE = 0.75;
const MAX_SHADE = 1.0;
// ---- vector math ----
const dot = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;
const scale = (v, s) => ({ x: v.x * s, y: v.y * s, z: v.z * s });
const add = (a, b) => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });
const neg = (v) => ({ x: -v.x, y: -v.y, z: -v.z });
const cross = (a, b) => ({
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
});
const normalize = (v) => {
    const len = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
    return len < 1e-9 ? { x: 0, y: 0, z: 1 } : scale(v, 1 / len);
};
/**
 * Projects a 3D world point onto the 2D view plane.
 *
 * Output is [-dot(p,up), dot(p,right)] so that after the PixBimViewer's
 * internal X/Y flip ([x,y] → [y,x]), the result maps to screen coordinates
 * where X = horizontal and Y = height (larger up-component = higher on screen).
 *
 * For side views where up = world Z this is identical to [-p.z, dot(p,right)].
 * For isometric views up is derived from cross(viewDir, right).
 */
const project2D = (p, right, up) => [
    -dot(p, up), // will become screen Y after viewer flip
    dot(p, right), // will become screen X after viewer flip
];
/**
 * Converts a view-space normal to a shade factor [MIN_SHADE, MAX_SHADE].
 */
const shadeFactor = (faceNormalWorld, right, up, viewDir, lightDir) => {
    const nx = dot(faceNormalWorld, right);
    const ny = dot(faceNormalWorld, up);
    const nz = dot(faceNormalWorld, neg(viewDir));
    const dotLight = nx * lightDir.x + ny * lightDir.y + nz * lightDir.z;
    const clamped = Math.max(0, dotLight);
    return MIN_SHADE + clamped * (MAX_SHADE - MIN_SHADE);
};
const obbFaces = (obb) => {
    const { centerMm: c, axisA, axisB, axisC, extentAMm: eA, extentBMm: eB, extentCMm: eC } = obb;
    // centerMm is stored in meters (consistent with routing code convention).
    // extentXMm values are in mm — convert to meters to match.
    const hA = scale(axisA, eA / 1000);
    const hB = scale(axisB, eB / 1000);
    const hC = scale(axisC, eC / 1000);
    // Face definitions: [normal, tangent1, tangent2, offset_from_center]
    const faceDefs = [
        [axisA, hB, hC, hA],
        [neg(axisA), hB, hC, neg(hA)],
        [axisB, hA, hC, hB],
        [neg(axisB), hA, hC, neg(hB)],
        [axisC, hA, hB, hC],
        [neg(axisC), hA, hB, neg(hC)],
    ];
    return faceDefs.map(([normal, t1, t2, offset]) => {
        const faceCenter = add(c, offset);
        const corners = [
            add(faceCenter, add(neg(t1), neg(t2))),
            add(faceCenter, add(t1, neg(t2))),
            add(faceCenter, add(t1, t2)),
            add(faceCenter, add(neg(t1), t2)),
        ];
        return { corners, normal };
    });
};
/**
 * Projects all OBBs for a given view direction.
 * Returns faces sorted furthest-first (painter's algorithm), back-faces culled.
 *
 * @param obbs     list of OBBs to project
 * @param viewDir  unit vector pointing FROM viewer TOWARD the scene (world space)
 * @param right    unit vector pointing right in the view plane (world space)
 * @param lightDir optional light direction in view space — defaults to (1,0,1) normalized
 */
export const projectObbsForDirection = (obbs, viewDir, right, lightDir = DEFAULT_LIGHT_DIR_VIEW) => {
    // Derive the view up from viewDir × right so isometric and side views both work correctly.
    // For side views (viewDir ⊥ worldZ, right ⊥ worldZ) this resolves to world Z.
    const up = normalize(cross(viewDir, right));
    const faces = [];
    for (const obb of obbs) {
        for (const face of obbFaces(obb)) {
            const normalDot = dot(face.normal, viewDir);
            if (normalDot > 1e-6)
                continue; // back-face cull — strict positive only, edge-on faces (dot≈0) pass through
            // Depth from face center (average of corners) for stable painter ordering
            const faceCenter = {
                x: (face.corners[0].x + face.corners[1].x + face.corners[2].x + face.corners[3].x) / 4,
                y: (face.corners[0].y + face.corners[1].y + face.corners[2].y + face.corners[3].y) / 4,
                z: (face.corners[0].z + face.corners[1].z + face.corners[2].z + face.corners[3].z) / 4,
            };
            const depth = dot(faceCenter, viewDir);
            const polygon = face.corners.map((corner) => project2D(corner, right, up));
            faces.push({
                polygon,
                shade: shadeFactor(face.normal, right, up, viewDir, lightDir),
                depth,
            });
        }
    }
    // Painter's algorithm: furthest first
    faces.sort((a, b) => b.depth - a.depth);
    return faces;
};
